import { computed, reactive } from 'vue'
import useWidgetOptions from '../widget-options'
import { mergeDeepRight } from 'ramda'
import { useOrchestratorApi } from '@/api/orchestrator'
import components, { type CustomCssConfig } from 'widget-components'
import { useWorkspace } from '../workflow'

const {
    getCssVariables,
    appendFont,
    applyCssVariables,
} = components.useCustomCss()

const cssConfig = reactive<CustomCssConfig>({})

const cssVarContainer = document.getElementById('app') || document.documentElement

const fetchCssConfig = async (): Promise<CustomCssConfig> => {
    const client = useOrchestratorApi()
    const { decodedToken } = useWorkspace()

    try {
        if (!decodedToken.value) throw new Error('empty decoded token')

        const { workspaceId, workflowDefinitionId } = decodedToken.value
        const response = await client.get(`workspaces/${workspaceId}/workflows/definition/${workflowDefinitionId}/style`)
        return response.data?.style || {}
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
    }

    return {}
}

const getConfig = async () => {
    const configFromOptions = useWidgetOptions().options.value.cssConfig || {}
    const configFromApi = await fetchCssConfig()
    Object.assign(cssConfig, mergeDeepRight(configFromOptions, configFromApi))
}

const setupCustomCss = async () => {
    const variables = useWidgetOptions().options.value.cssVariables

    if (Object.keys(variables || {}).length) {
        appendFont({ variables })
        applyCssVariables(variables!, cssVarContainer)

        // if variables are present we skip getting the deprecated style configuration
        return
    }

    await getConfig()
    appendFont({ config: cssConfig })

    applyCssVariables(
        getCssVariables(cssConfig), cssVarContainer,
    )
}

const setupCustomCssForPreview = () => {
    const { options: widgetOptions } = useWidgetOptions()
    const configFromOptions = widgetOptions.value.cssConfig || {}
    const variables = widgetOptions.value.cssVariables
    Object.assign(cssConfig, configFromOptions)
    appendFont({ config: cssConfig, variables })

    const cssVariables = Object.keys(variables || {}).length
        ? variables!
        : getCssVariables(cssConfig)

    applyCssVariables(cssVariables, cssVarContainer)
}

const cssSizeToNumPx = (size: string): number => {
    if (size.includes('px')) {
        return +size.replace('px', '')
    }

    if (size.includes('em')) {
        return +(
            size
                .replace('rem', '')
                .replace('em', '') || 0
        ) * 16

    }
    return 0
}

const getInputHeightNumPixels = () => cssSizeToNumPx(
    cssVarContainer.style.getPropertyValue('--tl-inputs-height'),
)

const getContainerHeightNumPixels = () => cssSizeToNumPx(
    cssVarContainer.style.getPropertyValue('--tl-container-height'),
)


const hasImageBackground = computed(() => {
    return useWidgetOptions().options.value.cssVariables?.['--tl-widget-background-url']
        || !!cssConfig.background?.url
        || cssConfig['--tl-widget-background-url']
})

export const useCustomCss = () => {
    return {
        setupCustomCss,
        setupCustomCssForPreview,
        getInputHeightNumPixels,
        getContainerHeightNumPixels,
        hasImageBackground,
    }
}
